import React, { useState, useEffect, useRef } from 'react';
import { Search, Building2, MapPin, Phone, Mail, X } from 'lucide-react';
import { Input } from './ui/input';
import InsuranceLogo from './InsuranceLogo';

const InsuranceSearchCombobox = ({ 
  value, 
  onChange, 
  onInsuranceSelect,
  className = '',
  showLogo = true // Neuer Parameter für Logo-Anzeige
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimeout = useRef(null);

  // Sync with external value
  useEffect(() => {
    if (value && value !== searchQuery) {
      setSearchQuery(value);
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search for insurances with debounce
  useEffect(() => {
    if (searchQuery.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    // Debounce search
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/haftpflichtversicherungen/search?q=${encodeURIComponent(searchQuery)}`
        );
        const data = await response.json();
        
        if (data.success) {
          setResults(data.data);
          setIsOpen(data.data.length > 0);
        }
      } catch (error) {
        console.error('Error searching insurances:', error);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [searchQuery]);

  const handleInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    onChange(query);
    setSelectedIndex(-1);
    setSelectedInsurance(null);
  };

  const handleSelectInsurance = (insurance) => {
    // Set the name in the input
    setSearchQuery(insurance.name);
    onChange(insurance.name);
    setSelectedInsurance(insurance);
    
    // Pass full insurance details to parent
    if (onInsuranceSelect) {
      onInsuranceSelect(insurance);
    }
    
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleSelectInsurance(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    onChange('');
    setResults([]);
    setIsOpen(false);
    setSelectedInsurance(null);
    if (onInsuranceSelect) {
      onInsuranceSelect(null);
    }
    inputRef.current?.focus();
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        {/* Logo oder Search Icon links */}
        {showLogo && selectedInsurance ? (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <InsuranceLogo 
              name={selectedInsurance.name} 
              size="xs"
              showFallback={true}
            />
          </div>
        ) : (
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        )}
        <Input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => searchQuery.length >= 2 && results.length > 0 && setIsOpen(true)}
          placeholder="Versicherung suchen (mind. 2 Zeichen)..."
          className="pl-10 pr-10 h-10"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {loading && (
          <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        )}
      </div>

      {/* Dropdown Results */}
      {isOpen && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-y-auto">
          <div className="p-2 space-y-1">
            {results.map((insurance, index) => (
              <div
                key={index}
                onClick={() => handleSelectInsurance(insurance)}
                className={`p-3 rounded-md cursor-pointer transition-colors ${
                  selectedIndex === index
                    ? 'bg-blue-50 border-l-2 border-blue-500'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Insurance Logo */}
                  {showLogo && (
                    <InsuranceLogo 
                      name={insurance.name} 
                      size="md"
                      className="flex-shrink-0 mt-0.5"
                    />
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {!showLogo && (
                        <Building2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      )}
                      <p className="font-semibold text-sm text-gray-900 truncate">
                        {insurance.name}
                      </p>
                    </div>
                    
                    {insurance.city && (
                      <div className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                        <MapPin className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">
                          {insurance.street && `${insurance.street}, `}
                          {insurance.plz} {insurance.city}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex flex-wrap gap-3 mt-1">
                      {insurance.phone && (
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Phone className="h-3 w-3" />
                          <span>{insurance.phone}</span>
                        </div>
                      )}
                      {insurance.email && (
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Mail className="h-3 w-3" />
                          <span className="truncate max-w-[200px]">{insurance.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Results */}
      {isOpen && !loading && searchQuery.length >= 2 && results.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-4 text-center text-gray-500 text-sm">
          <Building2 className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p>Keine Versicherung gefunden für "{searchQuery}"</p>
          <p className="text-xs mt-1">Versuchen Sie einen anderen Suchbegriff</p>
        </div>
      )}
    </div>
  );
};

export default InsuranceSearchCombobox;
